import { useEffect, useState } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, BookOpen, CreditCard, Award, Car, AlertCircle, MessageSquare, Settings, MapPin, History, BarChart, ChevronLeft, ChevronRight, List, Loader2, CheckCircle, Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useToast } from "@/components/ui/use-toast";
import { getEnrollmentsByUserId, getUserPayments } from "@/utils/firestoreService";
import { Enrollment as FirestoreEnrollment } from "@/utils/firestoreService";
import { Enrollment } from "@/types/courseTypes";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, addDays, subDays } from "date-fns";
import ScheduleClassDialog from "@/components/scheduling/ScheduleClassDialog";
import StudentBookingDialog from "@/components/student/StudentBookingDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface Payment {
  id: string;
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseId: string;
  courseName: string;
  amount: number;
  paymentMethod: 'card' | 'cash';
  status: 'paid' | 'pending';
  createdAt: Date;
  updatedAt: Date;
  enrollmentId: string;
  instructorId: string | null;
}

interface Schedule {
  id: string;
  date: string;
  time: string;
  startTime: string;
  endTime: string;
  instructorName: string;
  location: string;
  topic: string;
  courseName: string;
  duration: number;
  status: string;
  studentId: string;
}

const formatCurrency = (amount: number, _currency: string = 'GBP') => {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', currencyDisplay: 'symbol' }).format(amount).replace('GBP', '£');
};

const PaymentSummary = ({ payments, lastPayment }: { payments: Payment[], lastPayment: Payment | null }) => {
  const totalPaid = payments.reduce((sum, payment) => 
    payment.status === 'paid' ? sum + payment.amount : sum, 0
  );
  const totalAmount = payments.reduce((sum, payment) => 
    sum + payment.amount, 0
  );
  const pendingAmount = payments.reduce((sum, payment) => 
    payment.status === 'pending' ? sum + payment.amount : sum, 0
  );

  console.log("Payment calculations:", {
    totalPaid,
    totalAmount,
    pendingAmount,
    paymentsCount: payments.length,
    payments: payments.map(p => ({
      id: p.id,
      amount: p.amount,
      status: p.status,
      courseName: p.courseName
    }))
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center mb-4">
        <div className="mr-4 bg-gradient-to-br from-green-100 to-green-200 p-3 rounded-full">
          <CreditCard className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <div className="text-2xl font-bold">
            {formatCurrency(totalPaid)}
          </div>
          <div className="text-sm text-gray-600">Total paid</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 p-3 rounded-md">
          <div className="text-sm text-gray-600">Total Amount</div>
          <div className="text-lg font-semibold">
            {formatCurrency(totalAmount)}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded-md">
          <div className="text-sm text-gray-600">Pending Amount</div>
          <div className="text-lg font-semibold">
            {formatCurrency(pendingAmount)}
          </div>
        </div>
      </div>

      {lastPayment && (
        <div className="bg-gray-50 p-3 rounded-md mt-4">
          <p className="text-sm">
            <span className="font-medium">Last Payment:</span> {formatCurrency(lastPayment.amount)} for {lastPayment.courseName}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Processed on {format(new Date(lastPayment.createdAt), "PPP")}
          </p>
        </div>
      )}

      <Button asChild variant="outline" className="w-full">
        <Link to="/student-dashboard/payments">View Payment History</Link>
      </Button>
    </div>
  );
};

const StudentDashboard = () => {
  const { currentUser, getUserByEmail, getFirestore } = useFirebase();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [lastPayment, setLastPayment] = useState<Payment | null>(null);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>("");
  const [upcomingLessons, setUpcomingLessons] = useState<Schedule[]>([]);
  const [totalHours, setTotalHours] = useState(0);
  const [hoursCompleted, setHoursCompleted] = useState(0);
  const [hoursRemaining, setHoursRemaining] = useState(0);
  const [nextLesson, setNextLesson] = useState<Schedule | null>(null);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [courseOptions, setCourseOptions] = useState<Enrollment[]>([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [showNoEnrollmentDialog, setShowNoEnrollmentDialog] = useState(false);
  const [showStudentBookingDialog, setShowStudentBookingDialog] = useState(false);
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const navigate = useNavigate();

  // Add state for overview functionality
  const [overviewPeriod, setOverviewPeriod] = useState<'day' | 'week' | 'month'>('day');
  const [selectedOverviewDate, setSelectedOverviewDate] = useState<Date>(new Date());
  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Helper functions for overview period calculations
  const getDateRangeForPeriod = (date: Date, period: 'day' | 'week' | 'month') => {
    const start = new Date(date);
    const end = new Date(date);
    
    switch (period) {
      case 'day':
        start.setHours(0, 0, 0, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case 'week':
        // Start from Monday of the week
        const dayOfWeek = start.getDay();
        const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start.setDate(start.getDate() - daysToMonday);
        start.setHours(0, 0, 0, 0);
        // End on Sunday
        end.setDate(start.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        break;
      case 'month':
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        end.setMonth(end.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
    }
    
    return { start, end };
  };

  const getEventsForPeriod = (period: 'day' | 'week' | 'month', date: Date) => {
    const { start, end } = getDateRangeForPeriod(date, period);
    return upcomingLessons.filter(lesson => {
      const lessonDate = new Date(lesson.date);
      return lessonDate >= start && lessonDate <= end;
    });
  };

  const getOverviewTitle = (period: 'day' | 'week' | 'month', date: Date) => {
    switch (period) {
      case 'day':
        return format(date, 'EEEE, MMMM d, yyyy');
      case 'week':
        const { start, end } = getDateRangeForPeriod(date, period);
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      case 'month':
        return format(date, 'MMMM yyyy');
      default:
        return format(date, 'EEEE, MMMM d, yyyy');
    }
  };

  const getOverviewStats = (period: 'day' | 'week' | 'month', date: Date) => {
    const periodEvents = getEventsForPeriod(period, date);
    
    const totalLessons = periodEvents.length;
    const totalHours = periodEvents.reduce((total, event) => total + (event.duration || 1), 0);
    const confirmedLessons = periodEvents.filter(event => event.status === 'confirmed').length;
    const pendingLessons = periodEvents.filter(event => event.status === 'pending').length;
    const completedLessons = periodEvents.filter(event => event.status === 'completed').length;
    
    return {
      totalLessons,
      totalHours,
      confirmedLessons,
      pendingLessons,
      completedLessons
    };
  };

  const navigateOverviewDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedOverviewDate);
    switch (overviewPeriod) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    setSelectedOverviewDate(newDate);
  };

  // PDF Report Generation for Overview
  const generatePDFReport = async (period: 'day' | 'week' | 'month', date: Date) => {
    setIsGeneratingReport(true);
    try {
      const periodEvents = getEventsForPeriod(period, date);
      const stats = getOverviewStats(period, date);
      
      // Create HTML content for the report with enhanced styling
      const reportHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Student Dashboard Report - ${getOverviewTitle(period, date)}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 0; 
              padding: 0; 
              background: #f8fafc;
              color: #1e293b;
            }
            .container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }
            .header {
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
              color: white;
              padding: 40px 30px;
              text-align: center;
              position: relative;
              overflow: hidden;
            }
            .header::before {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/><circle cx="10" cy="60" r="0.5" fill="white" opacity="0.1"/><circle cx="90" cy="40" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
              opacity: 0.3;
            }
            .header h1 {
              font-size: 2.5em;
              font-weight: 700;
              margin-bottom: 10px;
              position: relative;
              z-index: 1;
            }
            .header h2 {
              font-size: 1.3em;
              font-weight: 400;
              opacity: 0.9;
              margin-bottom: 15px;
              position: relative;
              z-index: 1;
            }
            .header p {
              font-size: 0.9em;
              opacity: 0.8;
              position: relative;
              z-index: 1;
            }
            .content {
              padding: 40px 30px;
            }
            .stats-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
              gap: 20px;
              margin-bottom: 40px;
            }
            .stat-card {
              background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 25px 20px;
              text-align: center;
              box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
              transition: transform 0.2s ease;
            }
            .stat-card:hover {
              transform: translateY(-2px);
            }
            .stat-number {
              font-size: 2.5em;
              font-weight: 700;
              color: #2563eb;
              margin-bottom: 8px;
              display: block;
            }
            .stat-label {
              font-size: 0.9em;
              color: #64748b;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .section {
              margin-bottom: 40px;
            }
            .section-title {
              font-size: 1.5em;
              font-weight: 600;
              color: #1e293b;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e2e8f0;
              position: relative;
            }
            .section-title::after {
              content: '';
              position: absolute;
              bottom: -2px;
              left: 0;
              width: 60px;
              height: 2px;
              background: #2563eb;
            }
            .lessons-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
              background: white;
              border-radius: 8px;
              overflow: hidden;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            }
            .lessons-table th {
              background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
              color: white;
              padding: 15px 12px;
              text-align: left;
              font-weight: 600;
              font-size: 0.9em;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .lessons-table td {
              padding: 12px;
              border-bottom: 1px solid #e2e8f0;
              font-size: 0.9em;
            }
            .lessons-table tr:nth-child(even) {
              background: #f8fafc;
            }
            .lessons-table tr:hover {
              background: #f1f5f9;
            }
            .status-badge {
              padding: 4px 8px;
              border-radius: 6px;
              font-size: 0.8em;
              font-weight: 500;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .status-confirmed {
              background: #dcfce7;
              color: #166534;
            }
            .status-pending {
              background: #fef3c7;
              color: #92400e;
            }
            .status-completed {
              background: #dbeafe;
              color: #1e40af;
            }
            .status-cancelled {
              background: #fee2e2;
              color: #991b1b;
            }
            .empty-state {
              text-align: center;
              padding: 60px 20px;
              color: #64748b;
            }
            .empty-state h3 {
              font-size: 1.3em;
              margin-bottom: 10px;
              color: #475569;
            }
            .empty-state p {
              font-size: 0.95em;
              line-height: 1.6;
            }
            .footer {
              background: #f8fafc;
              padding: 30px;
              text-align: center;
              border-top: 1px solid #e2e8f0;
            }
            .footer p {
              color: #64748b;
              font-size: 0.9em;
            }
            @media print {
              body { background: white; }
              .container { box-shadow: none; }
              .stat-card:hover { transform: none; }
              .lessons-table tr:hover { background: inherit; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1>📊 Student Dashboard Report</h1>
              <h2>${getOverviewTitle(period, date)}</h2>
              <p>Generated on ${format(new Date(), 'EEEE, MMMM d, yyyy \'at\' h:mm a')}</p>
            </div>
            
            <div class="content">
              <div class="section">
                <h3 class="section-title">📈 Summary Statistics</h3>
                <div class="stats-grid">
                  <div class="stat-card">
                    <span class="stat-number">${stats.totalLessons}</span>
                    <div class="stat-label">Total Lessons</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.totalHours}</span>
                    <div class="stat-label">Total Hours</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.confirmedLessons}</span>
                    <div class="stat-label">Confirmed</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.pendingLessons}</span>
                    <div class="stat-label">Pending</div>
                  </div>
                  <div class="stat-card">
                    <span class="stat-number">${stats.completedLessons}</span>
                    <div class="stat-label">Completed</div>
                  </div>
                </div>
              </div>
              
              <div class="section">
                <h3 class="section-title">📋 Lesson Details</h3>
                ${periodEvents.length === 0 ? `
                  <div class="empty-state">
                    <h3>No Lessons Found</h3>
                    <p>No lessons were scheduled for ${period === 'day' ? 'this day' : period === 'week' ? 'this week' : 'this month'}.</p>
                  </div>
                ` : `
                  <table class="lessons-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Course</th>
                        <th>Instructor</th>
                        <th>Duration</th>
                        <th>Status</th>
                        <th>Venue</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${periodEvents.map(lesson => `
                        <tr>
                          <td>${format(new Date(lesson.date), 'EEEE, MMMM d, yyyy')}</td>
                          <td>${lesson.startTime} - ${lesson.endTime}</td>
                          <td>${lesson.courseName}</td>
                          <td>${lesson.instructorName}</td>
                          <td>${lesson.duration} hour${lesson.duration !== 1 ? 's' : ''}</td>
                          <td><span class="status-badge status-${lesson.status}">${lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}</span></td>
                          <td>${lesson.location || 'Not specified'}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                `}
              </div>
            </div>
            
            <div class="footer">
              <p>© ${new Date().getFullYear()} DriveWise School. This report was generated automatically.</p>
            </div>
          </div>
        </body>
        </html>
      `;

      // Create blob and download
      const blob = new Blob([reportHTML], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-dashboard-report-${period}-${format(date, 'yyyy-MM-dd')}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Report Generated",
        description: `Your ${period} dashboard report has been downloaded successfully.`,
      });
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      try {
        console.log("Fetching data for user:", currentUser.uid);
        
        // Fetch student data
        const userData = await getUserByEmail(currentUser.email || '');
        if (userData) {
          setStudentProfile({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
            instructorId: userData.instructorId || '',
            area: userData.area || '',
            phone: userData.phone || '',
            address: userData.address || '',
            postcode: userData.postcode || ''
          });
        }
        
        // Fetch enrollments
        const fetchedEnrollments = await getEnrollmentsByUserId(currentUser.uid);
        console.log("Fetched enrollments:", fetchedEnrollments);
        
        // Filter enrollments to include public bookings and regular enrollments
        const validEnrollments = fetchedEnrollments.filter(enrollment => {
          const isValid = enrollment && 
            enrollment.userId === currentUser.uid &&
            enrollment.courseId &&
            enrollment.courseName &&
            enrollment.courseName !== 'Unknown Course';
          
          if (!isValid) {
            console.log("Filtered out invalid enrollment:", enrollment);
          }
          
          return isValid;
        });
        
        console.log("Valid enrollments after filtering:", validEnrollments);
        
        // Convert enrollments to match the expected interface
        const convertedEnrollments = validEnrollments.map(enrollment => ({
          ...enrollment,
          paymentStatus: enrollment.paymentStatus || 'pending',
          enrollmentStatus: enrollment.enrollmentStatus || 'pending'
        }));
        
        setEnrollments(convertedEnrollments);

        // Fetch payments
        const fetchedPayments = await getUserPayments(currentUser.uid);
        console.log("Fetched payments:", fetchedPayments);
        
        // Convert payments to match the expected interface
        const convertedPayments = fetchedPayments.map(payment => ({
          id: payment.id,
          studentId: payment.userId || currentUser.uid,
          studentName: payment.userName || currentUser.displayName || "Student",
          studentEmail: payment.userEmail || currentUser.email || "",
          courseId: payment.courseId,
          courseName: payment.courseName,
          amount: payment.amount,
          paymentMethod: payment.paymentMethod as 'card' | 'cash',
          status: payment.status as 'paid' | 'pending',
          createdAt: new Date(payment.createdAt),
          updatedAt: new Date(payment.createdAt), // Using createdAt as updatedAt
          enrollmentId: payment.enrollmentId,
          instructorId: payment.instructorId || null
        }));
        
        setPayments(convertedPayments);

        // Set last payment
        if (convertedPayments.length > 0) {
          setLastPayment(convertedPayments[0]);
        }

        // Fetch schedules
        const db = getFirestore();
        const schedulesRef = collection(db, "schedules");
        const schedulesQuery = query(
          schedulesRef,
          where("studentId", "==", currentUser.uid),
          where("status", "in", ["pending", "confirmed"]),
          orderBy("date", "asc")
        );
        const schedulesSnapshot = await getDocs(schedulesQuery);
        
        const schedulesData = schedulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Schedule[];
        
        console.log("Fetched schedules:", schedulesData);
        setUpcomingLessons(schedulesData);

        // Calculate totals
        const totalHours = convertedEnrollments.reduce((sum, enrollment) => sum + (enrollment.totalHours || 0), 0);
        const hoursCompleted = convertedEnrollments.reduce((sum, enrollment) => sum + (enrollment.hoursTaken || 0), 0);
        const hoursRemaining = convertedEnrollments.reduce((sum, enrollment) => sum + (enrollment.hoursRemaining || 0), 0);

        setTotalHours(totalHours);
        setHoursCompleted(hoursCompleted);
        setHoursRemaining(hoursRemaining);

        // Set next lesson
        const futureSchedules = schedulesData.filter(schedule => 
          new Date(schedule.date) > new Date() && schedule.status === "confirmed"
        );
        
        if (futureSchedules.length > 0) {
          setNextLesson(futureSchedules[0]);
        }

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser, toast]);

  const progressPercent = totalHours > 0 ? Math.round((hoursCompleted / totalHours) * 100) : 0;

  const studentData = {
    name: currentUser?.displayName || "Student",
    upcomingLesson: nextLesson && nextLesson.date ? {
      date: format(new Date(nextLesson.date), "EEEE, d MMMM yyyy"),
      time: `${nextLesson.startTime} - ${nextLesson.endTime}`,
      instructor: nextLesson.instructorName,
      location: nextLesson.location,
      topic: nextLesson.topic,
    } : null,
    payments: {
      totalPaid: lastPayment ? lastPayment.amount : 0,
      lastPayment: lastPayment && lastPayment.createdAt ? {
        amount: lastPayment.amount,
        date: format(new Date(lastPayment.createdAt), "dd MMM yyyy"),
        for: lastPayment.courseName
      } : null
    }
  };

  const handleBookLesson = async () => {
    console.log("Book Lesson button clicked");
    try {
      setIsLoadingCourses(true);
      console.log("Fetching enrollments for user:", currentUser?.uid);
      
      // Fetch user's enrollments
      const userEnrollments = await getEnrollmentsByUserId(currentUser?.uid || "");
      console.log("Fetched enrollments:", userEnrollments);
      
      // Filter for valid enrollments (must have instructor and be in correct status)
      const validEnrollments = userEnrollments.filter(
        (enrollment) => 
          enrollment.instructorId && 
          (enrollment.enrollmentStatus === "enrolled" || enrollment.status === "assigned") &&
          enrollment.hoursRemaining > 0
      );
      console.log("Valid enrollments:", validEnrollments);

      if (validEnrollments.length === 0) {
        console.log("No valid enrollments found, showing dialog");
        setShowNoEnrollmentDialog(true);
        return;
      }

      if (validEnrollments.length === 1) {
        console.log("Single valid enrollment found, opening booking dialog");
        setSelectedEnrollmentId(validEnrollments[0].id);
        setIsBookingOpen(true);
        return;
      }

      // Multiple enrollments: show course selection dialog
      console.log("Multiple enrollments found, showing course selection");
      setCourseOptions(validEnrollments as Enrollment[]);
      setShowCourseDialog(true);
    } catch (error) {
      console.error("Error in handleBookLesson:", error);
      toast({
        title: "Error",
        description: "Failed to prepare booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingCourses(false);
    }
  };

  const handleSelectCourse = (enrollmentId: string) => {
    setSelectedEnrollmentId(enrollmentId);
    setShowCourseDialog(false);
    setIsBookingOpen(true);
  };

  // Find the hoursRemaining for the selected enrollment
  const selectedEnrollment = enrollments.find(e => e.id === selectedEnrollmentId);
  const selectedHoursRemaining = selectedEnrollment ? selectedEnrollment.hoursRemaining || 0 : 0;

  return (
    <DashboardLayout role="student">
      <div className="mb-8 space-y-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex justify-between items-center"
        >
          <div>
            <h1 className="text-3xl font-bold text-gray-900 bg-clip-text text-transparent bg-gradient-to-r from-drivewise-900 to-blue-700">
              Welcome back, {studentData.name}
            </h1>
            <p className="text-gray-600">
              Track your progress, manage your lessons, and access learning resources from your dashboard.
            </p>
          </div>
          <Button
            onClick={() => {
              console.log("Button clicked, current state:", {
                isLoadingCourses,
                currentUser: currentUser?.uid,
                enrollmentsCount: enrollments.length
              });
              handleBookLesson();
            }}
            className="w-full bg-gradient-to-r from-drivewise-600 to-purple-600 hover:from-drivewise-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
            disabled={isLoadingCourses}
          >
            {isLoadingCourses ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Loading...</span>
              </div>
            ) : (
              "Book a Lesson"
            )}
          </Button>
        </motion.div>
      </div>

      {/* Student Overview Section - Moved to top */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
        className="mb-8"
      >
        <Card className="border-t-4 border-drivewise-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CardTitle className="text-xl flex items-center gap-2">
                  <BarChart className="h-5 w-5 text-drivewise-600" />
                  My Overview - {getOverviewTitle(overviewPeriod, selectedOverviewDate)}
                </CardTitle>
                
                {/* Period Selector */}
                <div className="flex items-center gap-2">
                  <Button
                    variant={overviewPeriod === 'day' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOverviewPeriod('day')}
                  >
                    Day
                  </Button>
                  <Button
                    variant={overviewPeriod === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOverviewPeriod('week')}
                  >
                    Week
                  </Button>
                  <Button
                    variant={overviewPeriod === 'month' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setOverviewPeriod('month')}
                  >
                    Month
                  </Button>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateOverviewDate('prev')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedOverviewDate(new Date())}
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateOverviewDate('next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => generatePDFReport(overviewPeriod, selectedOverviewDate)}
                  disabled={isGeneratingReport}
                  className="flex items-center gap-2"
                >
                  {isGeneratingReport ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                  {isGeneratingReport ? 'Generating...' : 'Download Report'}
                </Button>
              </div>
            </div>
          </CardHeader>
          
          <CardContent>
            {/* Overview Statistics */}
            {(() => {
              const stats = getOverviewStats(overviewPeriod, selectedOverviewDate);
              const periodEvents = getEventsForPeriod(overviewPeriod, selectedOverviewDate);
              
              return (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-700">Total Lessons</p>
                            <p className="text-2xl font-bold text-blue-900">
                              {stats.totalLessons}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                            <Calendar className="h-6 w-6 text-blue-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-700">Total Hours</p>
                            <p className="text-2xl font-bold text-green-900">
                              {stats.totalHours}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                            <Clock className="h-6 w-6 text-green-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-purple-700">Confirmed</p>
                            <p className="text-2xl font-bold text-purple-900">
                              {stats.confirmedLessons}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-purple-200 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-6 w-6 text-purple-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-orange-700">Pending</p>
                            <p className="text-2xl font-bold text-orange-900">
                              {stats.pendingLessons}
                            </p>
                          </div>
                          <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                            <AlertCircle className="h-6 w-6 text-orange-700" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Lessons List */}
                  <div className="space-y-4">
                    <div 
                      className="flex items-center justify-between cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                      onClick={() => setIsOverviewCollapsed(!isOverviewCollapsed)}
                    >
                      <h4 className="text-lg font-semibold flex items-center gap-2">
                        <List className="h-5 w-5 text-drivewise-600" />
                        {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}s Lessons
                        <span className="text-sm font-normal text-gray-500">
                          ({periodEvents.length} lessons)
                        </span>
                      </h4>
                      {isOverviewCollapsed ? (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronLeft className="h-5 w-5 text-gray-500" />
                      )}
                    </div>
                    
                    {!isOverviewCollapsed && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {(() => {
                          const sortedEvents = periodEvents
                            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                          if (sortedEvents.length === 0) {
                            return (
                              <Card className="col-span-full">
                                <CardContent className="p-6 text-center">
                                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                  <h5 className="text-lg font-medium text-gray-900 mb-2">
                                    No Lessons {overviewPeriod === 'day' ? 'Today' : overviewPeriod === 'week' ? 'This Week' : 'This Month'}
                                  </h5>
                                  <p className="text-gray-500 mb-4">
                                    {overviewPeriod === 'day' ? 'You have no lessons scheduled for today.' : 
                                     overviewPeriod === 'week' ? 'You have no lessons scheduled this week.' : 
                                     'You have no lessons scheduled this month.'}
                                  </p>
                                  <Button onClick={handleBookLesson}>
                                    Book a Lesson
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          }

                          return sortedEvents.map((lesson, index) => (
                            <Card key={lesson.id} className="hover:shadow-md transition-shadow">
                              <CardContent className="p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <h5 className="font-semibold text-gray-900">{lesson.courseName}</h5>
                                    <p className="text-sm text-gray-600">{lesson.instructorName}</p>
                                  </div>
                                  <Badge className={
                                    lesson.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                    lesson.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    lesson.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                    'bg-gray-100 text-gray-800'
                                  }>
                                    {lesson.status.charAt(0).toUpperCase() + lesson.status.slice(1)}
                                  </Badge>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                  <div className="flex items-center gap-2">
                                    <Calendar className="h-4 w-4 text-gray-500" />
                                    <span>{format(new Date(lesson.date), 'EEEE, MMMM d, yyyy')}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <span>{lesson.startTime} - {lesson.endTime} ({lesson.duration} hour{lesson.duration !== 1 ? 's' : ''})</span>
                                  </div>
                                  {lesson.location && (
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-4 w-4 text-gray-500" />
                                      <span>{lesson.location}</span>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex gap-2 mt-4">
                                  <Button variant="outline" size="sm" asChild className="flex-1">
                                    <Link to="/student-dashboard/schedules">View Details</Link>
                                  </Button>
                                  {lesson.status === 'pending' && (
                                    <Button variant="outline" size="sm" className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50">
                                      Cancel
                                    </Button>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </motion.div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-3 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="md:col-span-3"
        >
          <Card className="border-t-4 border-purple-500 bg-gradient-to-br from-white to-purple-50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl">My Lessons</CardTitle>
                <CardDescription>Your current driving lessons</CardDescription>
              </div>
              <Button asChild variant="ghost" size="sm">
                <Link to="/student-dashboard/my-enrollments">View All</Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-pulse flex flex-col items-center">
                    <div className="h-10 w-10 mb-4 rounded-full bg-gray-200"></div>
                    <div className="h-4 w-32 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ) : enrollments.length > 0 ? (
                <div className="space-y-4">
                  {enrollments.slice(0, 3).map((enrollment, index) => (
                    <motion.div
                      key={enrollment.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                      className="p-3 rounded-lg border border-gray-100 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-md ${
                            enrollment.courseType === "theory" ? "bg-blue-100" : "bg-green-100"
                          }`}>
                            {enrollment.courseType === "theory" ? (
                              <BookOpen className="h-5 w-5 text-blue-600" />
                            ) : (
                              <Car className="h-5 w-5 text-green-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-medium">{enrollment.courseName}</h3>
                            <div className="flex items-center mt-1">
                              <Badge className={
                                enrollment.enrollmentStatus === "completed" ? "bg-green-500" :
                                enrollment.enrollmentStatus === "enrolled" ? "bg-blue-500" :
                                enrollment.status === "assigned" ? "bg-blue-500" :
                                "bg-yellow-500"
                              }>
                                {enrollment.enrollmentStatus === "enrolled" || enrollment.status === "assigned" 
                                  ? "Active" 
                                  : enrollment.enrollmentStatus.charAt(0).toUpperCase() + enrollment.enrollmentStatus.slice(1)}
                              </Badge>
                              
                              {enrollment.instructorName && (
                                <span className="ml-2 text-xs text-gray-500">
                                  Instructor: {enrollment.instructorName}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          asChild
                          size="sm"
                          variant="ghost"
                          className="text-drivewise-600 hover:text-drivewise-800 hover:bg-drivewise-50"
                        >
                          <Link to={
                            enrollment.courseType === "theory"
                              ? `/student-dashboard/theory-course/${enrollment.courseId}`
                              : `/student-dashboard/practical-course/${enrollment.courseId}`
                          }>
                            View
                          </Link>
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                  
                  {enrollments.length > 3 && (
                    <p className="text-center text-sm text-gray-500">
                      +{enrollments.length - 3} more lessons
                    </p>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <BookOpen className="h-10 w-10 text-gray-300 mb-2" />
                  <p className="text-gray-500">You haven't enrolled in any lessons yet.</p>
                  <Button 
                    className="mt-4 bg-gradient-to-r from-drivewise-600 to-purple-600"
                    onClick={() => setShowStudentBookingDialog(true)}
                  >
                    Book Your First Lesson
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid gap-6 mb-8 grid-cols-1 md:grid-cols-3">
        <motion.div
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Card className="border-t-4 border-drivewise-500 bg-gradient-to-br from-white to-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Driving Hours</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Overall progress</span>
                  <span className="text-sm font-medium">{progressPercent}%</span>
                </div>
                <Progress value={progressPercent} className="h-2 bg-blue-100" />
                
                {loading ? (
                  <div className="text-sm text-gray-600 mt-4">Loading your data...</div>
                ) : totalHours > 0 ? (
                  <div className="grid grid-cols-3 gap-2 text-center mt-4">
                    <div className="bg-gray-50 p-2 rounded-md">
                      <div className="text-lg font-semibold text-drivewise-700">{totalHours}</div>
                      <div className="text-xs text-gray-500">Total Hours</div>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-md">
                      <div className="text-lg font-semibold text-blue-700">{hoursCompleted}</div>
                      <div className="text-xs text-gray-500">Completed</div>
                    </div>
                    <div className="bg-green-50 p-2 rounded-md">
                      <div className="text-lg font-semibold text-green-700">{hoursRemaining}</div>
                      <div className="text-xs text-gray-500">Remaining</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 mt-4">
                    No driving hours recorded yet. Enroll in a practical course to get started.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Card className="border-t-4 border-green-500 bg-gradient-to-br from-white to-green-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Payment Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <PaymentSummary payments={payments} lastPayment={lastPayment} />
            </CardContent>
          </Card>
        </motion.div>

        <motion.div
          whileHover={{ y: -5 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <Card className="border-t-4 border-blue-500 bg-gradient-to-br from-white to-blue-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Next Lesson</CardTitle>
              <CardDescription>
                {nextLesson ? (
                  <span>Upcoming</span>
                ) : (
                  <span>No upcoming lessons</span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {nextLesson ? (
                <div className="space-y-3">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{format(new Date(nextLesson.date), "EEEE, MMMM d, yyyy")}</span>
                  </div>
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{nextLesson.startTime} - {nextLesson.endTime} ({nextLesson.duration} {nextLesson.duration === 1 ? "hour" : "hours"})</span>
                  </div>
                  <div className="flex items-start">
                    <Car className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">with {nextLesson.instructorName}</span>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span className="text-sm">{nextLesson.location}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {nextLesson.courseName}
                  </div>
                  <div className="flex justify-between mt-3">
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/student-dashboard/schedules">View Details</Link>
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50">
                      Reschedule
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No upcoming lessons scheduled</p>
                  <Button onClick={handleBookLesson}>
                    Book a Lesson
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <Card className="bg-gradient-to-br from-white to-gray-50">
            <CardHeader>
              <CardTitle className="text-xl">Quick Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/courses">
                      <BookOpen className="mr-2 h-5 w-5 text-blue-600" />
                      Available Courses
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/my-enrollments">
                      <BookOpen className="mr-2 h-5 w-5 text-green-600" />
                      My Courses
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/schedules">
                      <Calendar className="mr-2 h-5 w-5 text-amber-600" />
                      My Schedules
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/bookings">
                      <Clock className="mr-2 h-5 w-5 text-purple-600" />
                      Book a Lesson
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/lesson-history">
                      <History className="mr-2 h-5 w-5 text-purple-600" />
                      Lesson History
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/messages">
                      <MessageSquare className="mr-2 h-5 w-5 text-amber-600" />
                      Messages
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/payment-history">
                      <CreditCard className="mr-2 h-5 w-5 text-amber-600" />
                      Payment History
                    </Link>
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button asChild variant="outline" className="w-full justify-start bg-white hover:bg-gray-50">
                    <Link to="/student-dashboard/settings">
                      <Settings className="mr-2 h-5 w-5 text-amber-600" />
                      Settings
                    </Link>
                  </Button>
                </motion.div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {showNoEnrollmentDialog && (
        <Dialog open={showNoEnrollmentDialog} onOpenChange={setShowNoEnrollmentDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>No Enrollment Found</DialogTitle>
              <DialogDescription>
                You need to enroll in a course before you can book a lesson.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-4">
                <BookOpen className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-center text-gray-600">
                  You need to enroll in a course before booking lessons.
                </p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowNoEnrollmentDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowNoEnrollmentDialog(false);
                    setShowStudentBookingDialog(true);
                  }}
                >
                  Book Your First Lesson
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showCourseDialog && (
        <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select a Course</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Label>Select which course you want to book a lesson for:</Label>
              {courseOptions.map((enrollment) => (
                <Button
                  key={enrollment.id}
                  className="w-full justify-start mb-2"
                  variant="outline"
                  onClick={() => handleSelectCourse(enrollment.id)}
                >
                  {enrollment.courseName} with {enrollment.instructorName}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
      {selectedEnrollmentId && (
        <ScheduleClassDialog
          open={isBookingOpen}
          onOpenChange={setIsBookingOpen}
          enrollmentId={selectedEnrollmentId}
          courseId={enrollments.find(e => e.id === selectedEnrollmentId)?.courseId || ""}
          hoursRemaining={enrollments.find(e => e.id === selectedEnrollmentId)?.hoursRemaining || 0}
          onScheduled={() => {
            setIsBookingOpen(false);
            // The useEffect will automatically refetch data
          }}
        />
      )}

      {studentProfile && (
        <StudentBookingDialog
          open={showStudentBookingDialog}
          onOpenChange={setShowStudentBookingDialog}
          studentData={studentProfile}
          onBookingComplete={() => {
            // Refetch data to show new enrollment
            window.location.reload();
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default StudentDashboard;
